'use client'

import { useSuspenseQuery } from '@rocicorp/zero/react'
import { notFound, useRouteContext } from '@tanstack/react-router'

import { queries } from '#zero/queries.ts'
import {
  type ZeroWorkspaceBySlugRow,
  workspaceFromZero,
} from '#zero/workspace-from-zero.ts'
import type { ZeroQueryContext } from '#zero/zero-default-types.ts'

import { useWorkspace } from './use-workspace'

export const useCurrentWorkspace = () => {
  const slug = useWorkspace()
  // Resolved by /_app beforeLoad (awaited session) — always present, so the
  // membership check in the mapper never runs without auth context.
  const { user } = useRouteContext({
    from: '/_app',
  })

  const ctx: ZeroQueryContext = {
    user: {
      id: user.id,
      email: user.email,
      name: user.name,
      image: user.image,
      emailVerified: user.emailVerified,
    },
  }

  // Suspend until the query result is `complete` (server-confirmed) rather
  // than the default `partial`, which resolves on any local/partial data.
  // Without this, a page refresh evaluates the workspace against an empty or
  // not-yet-synced local store, sees no row, and throws a premature 404.
  // Once `complete`, an absent row authoritatively means "does not exist".
  const [row] = useSuspenseQuery(queries.workspaces.bySlug({ slug }), {
    suspendUntil: 'complete',
  })

  if (slug.length === 0) {
    throw notFound()
  }

  const workspace = workspaceFromZero(
    row as ZeroWorkspaceBySlugRow | undefined,
    ctx,
  )

  if (workspace === null) {
    throw notFound()
  }

  return [workspace] as const
}
